import CryptoJS from "crypto-js";
import { keyConstants } from "./keyConstants";
import { persistor } from "store";
import jwt_decode from "jwt-decode";

export const isAuthenticated = () => {
  const now = Date.now();
  const expireTime = +(
    localStorage.getItem(keyConstants.EXPIRY_TOKEN_TIME) || 0
  );
  const token = localStorage.getItem(keyConstants.EXPIRY_TOKEN_TIME);
  const sessionIsValid = expireTime > now && !!token;
  return sessionIsValid;
};
export const isUnAuthenticated = () => {
  const now = Date.now();
  const expireTime = +(
    localStorage.getItem(keyConstants.EXPIRY_TOKEN_TIME) || 0
  );
  const token = localStorage.getItem(keyConstants.EXPIRY_TOKEN_TIME);
  const sessionIsInvalid = expireTime <= now || !token;
  return sessionIsInvalid;
};

export const handleLogout = () => {
  localStorage.removeItem(keyConstants.EXPIRY_TOKEN_TIME);
  localStorage.removeItem(keyConstants.AUTH_TOKEN);
  persistor.purge();
  window.location.href = window.location.origin + "/auth";
};

export const isEmptyObject = (obj: Record<string, string>) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const removeNull = <T>(object: T) => {
  const newObject = { ...object };
  for (const key in newObject) {
    if (Object.prototype.hasOwnProperty.call(newObject, key)) {
      const element = newObject[key];
      if (
        typeof element === "undefined" ||
        element === null ||
        (typeof element === "string" && !element)
      ) {
        delete newObject[key];
      }
    }
  }
  return newObject as T;
};

export const buildQueryParam = (json: Record<any, string>) => {
  if (isEmptyObject(json)) {
    return "";
  }

  let query = "?";
  for (const key in removeNull(json)) {
    query +=
      encodeURIComponent(key) + "=" + encodeURIComponent(json[key]) + "&";
  }
  return query.replace(/&$/g, "");
};

export const setCookies = (
  cookies: Array<{ name: string; value: string }>,
  days = 1,
  path?: string,
) => {
  cookies.forEach((cookie) => setCookie(cookie.name, cookie.value, days, path));
};

export const setCookie = (
  name: string,
  value: string,
  days = 1,
  path = "/",
) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  window.document.cookie = name + "=" + value + ";" + expires + ";path=" + path;
};

export const getCookie = (cname: string) => {
  let name = cname + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const jsonToBase64 = (object: Record<any, any>) => {
  const json = JSON.stringify(object);
  return Buffer.from(json).toString("base64");
};

export const base64ToJson = (base64String: string) => {
  const json = Buffer.from(base64String, "base64").toString();
  return JSON.parse(json);
};

export const encrypt = (secret: string, value: string) => {
  return CryptoJS.AES.encrypt(value, secret).toString();
};

export const decrypt = (secret: string, cipherText: string) => {
  return CryptoJS.AES.decrypt(cipherText, secret).toString(CryptoJS.enc.Utf8);
};

export const getCountryFlag = (
  isoCode: string,
  size: "24x18" | "48x36" = "48x36",
) => {
  return `https://flagcdn.com/${size}/${isoCode.toLowerCase()}.png`;
};

export const decodeJwt = <T>(jwt: string) => {
  try {
    const decoded = jwt_decode<T>(jwt);
    return decoded;
  } catch (err) {
    return null;
  }
};

export const formateAmount = (textValue: string | number) => {
  let value: string = "0";
  if (textValue) {
    const hasSign = Number(textValue) < 0;
    const [whole, decimal] = textValue.toString().trim().split(".");
    const hasDecimal = typeof decimal !== "undefined";
    const formattedWhole = whole
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    value = `${hasSign ? "-" : ""}${formattedWhole}${
      hasDecimal ? "." + decimal.replace(/\D/g, "") : ""
    }`;
  }
  return value;
};

export const getFormattedAmountValue = (textValue: string) => {
  if (textValue) {
    const [whole, decimal] = textValue.trim().split(".");
    const hasDecimal = typeof decimal !== "undefined";
    const formattedWhole = whole.replace(/\D/g, "");
    return formattedWhole + (hasDecimal ? `.${decimal}` : "");
  }
  return "";
};

export const cloneDeep = <T extends Object>(object: T) => {
  let objCopy = JSON.parse(JSON.stringify(object));
  return objCopy as T;
};

export const calcFileSize = (bytes: number) => {
  let byteText = "";
  if (bytes >= 1073741824) {
    // 1024 * 1024 * 1024
    byteText = (bytes / 1073741824).toFixed(2) + " GB";
  } else if (bytes >= 1048576) {
    // 1024 * 1024
    byteText = (bytes / 1048576).toFixed(2) + " MB";
  } else if (bytes >= 1024) {
    // 1024 * 1
    byteText = (bytes / 1024).toFixed(2) + " KB";
  } else if (bytes > 1) {
    byteText = bytes.toFixed(2) + " bytes";
  } else if (bytes === 1) {
    byteText = bytes.toFixed(2) + " byte";
  } else {
    byteText = "0 bytes";
  }

  return byteText;
};

export const reduceObject = <T extends Object>(object: T) => {
  return Object.assign<T, any>(
    {},
    // @ts-ignore
    ...(function _flatten(o) {
      return [].concat(
        ...Object.keys(o)
          // @ts-ignore
          .map((k) =>
            // @ts-ignore
            typeof o[k] === "object"
              ? // @ts-ignore
                _flatten(o[k])
              : // @ts-ignore
                { [k]: o[k] },
          ),
      );
    })(object),
  );
};

export const isProd = () => {
  return process.env.REACT_APP_BASE_URL?.includes("api.onah.africa");
};

export function logErrorToMyService(error: Error, errorInfo: string) {
  console.log("Uncaught error:", error, errorInfo);
}
